import { useState, useEffect } from 'react'
import { ShoppingCart, AlertCircle } from 'lucide-react'
import { offerApi } from '../../lib/funnel-api'
import type { Offer } from '../../lib/funnel-api'

interface OfferPreviewProps {
  offerId: string
}

export function OfferPreview({ offerId }: OfferPreviewProps) {
  const [offer, setOffer] = useState<Offer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let mounted = true

    async function fetchOffer() {
      try {
        setLoading(true)
        setError(false)
        const response = await offerApi.get(offerId)
        if (mounted) {
          setOffer(response.data)
        }
      } catch (err) {
        if (mounted) {
          setError(true)
          console.error('Failed to fetch offer:', err)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    fetchOffer()

    return () => {
      mounted = false
    }
  }, [offerId])

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 mt-1 p-2 rounded border border-fn-border bg-fn-muted"
        role="status"
        aria-label="Loading offer details"
      >
        <div
          className="w-4 h-4 rounded-full animate-pulse bg-fn-text-muted"
        />
        <span className="text-xs text-fn-text-muted">
          Loading offer...
        </span>
      </div>
    )
  }

  if (error || !offer) {
    return (
      <div
        className="flex items-center gap-2 mt-1 p-2 rounded border"
        style={{
          borderColor: 'var(--fn-border)',
          backgroundColor: 'var(--fn-bg-muted)',
        }}
        role="alert"
      >
        <AlertCircle className="w-4 h-4 text-fn-error" />
        <span className="text-xs text-fn-text-muted">
          Offer not found
        </span>
      </div>
    )
  }

  const formatDiscount = () => {
    if (offer.discountType === 'percentage') {
      return `${offer.discountValue}% off`
    } else if (offer.discountType === 'fixed') {
      return `$${offer.discountValue} off`
    } else if (offer.discountType === 'bogo') {
      return 'BOGO'
    }
    return 'Discount'
  }

  return (
    <div
      className="flex items-center gap-2 mt-1 p-2 rounded border"
      style={{
        borderColor: 'var(--fn-border)',
        backgroundColor: 'var(--fn-bg-card)',
      }}
      role="region"
      aria-label={`Offer details: ${offer.name}`}
    >
      <ShoppingCart
        className="w-4 h-4 flex-shrink-0 text-fn-primary"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-medium truncate text-fn-text"
            title={offer.name}
          >
            {offer.name}
          </span>
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0"
            style={{
              backgroundColor: offer.isActive
                ? 'var(--fn-success-light)'
                : 'var(--fn-bg-muted)',
              color: offer.isActive
                ? 'var(--fn-success)'
                : 'var(--fn-text-muted)',
            }}
            role="status"
            aria-label={offer.isActive ? 'Active offer' : 'Inactive offer'}
          >
            {offer.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <span className="text-xs block mt-0.5 text-fn-text-muted">
          {formatDiscount()}
        </span>
      </div>
    </div>
  )
}
